"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * One hold length for every touch context menu (Dallas 2026-10-07), counted
 * from the finger going down. Was 300ms *after* a 120ms arm wait (420ms); the
 * media host used 650+120 = 770ms, row wrappers 480/520ms.
 */
export const LONG_PRESS_MENU_MS = 350;
const DEFAULT_DELAY = LONG_PRESS_MENU_MS;
const DEFAULT_PICKUP_DELAY = 180;
/** Wait before arming hold timers so list scroll can commit first. The menu
 *  timer subtracts this, so `delay` is always measured from touchstart. */
const HOLD_ARM_DELAY_MS = 120;

function menuDelayAfterArm(delay: number) {
  return Math.max(0, delay - HOLD_ARM_DELAY_MS);
}
/** Cancel pickup/menu if finger moves before pickup arms (keep low so list scroll wins). */
const PRE_PICKUP_MOVE = 6;
/** Vertical pan slop — match scroll intent before hold timers arm. */
const PAN_SLOP_PX = 4;
/**
 * After pickup, only promote to drag once the finger clearly moves.
 * Keep this tight so drag feels instant after the short hold.
 */
const POST_PICKUP_DRAG_MOVE = 18;

type Coords = { x: number; y: number };

type UseLongPressOptions = {
  /** Context-menu delay (ms) from touchstart. Default `LONG_PRESS_MENU_MS`. */
  delay?: number;
  /** Optional earlier “pickup” delay for drag-arming (ms). */
  pickupDelay?: number;
  /** Fires once at pickupDelay while still held — drag can begin after this. */
  onPickup?: (coords: Coords) => void;
  /**
   * Fires once when the finger moves past the drag threshold after pickup.
   * Use this to start a touch-drag session (HTML5 DnD does not work on mobile).
   */
  onDragIntent?: (coords: Coords) => void;
  /** Fires when the context-menu hold arms (before release). */
  onMenuArmed?: (coords: Coords) => void;
  /**
   * Hold/menu aborted because scroll or pan won. Tear down any pickup side
   * effects (Files touch-hold arm). Not called once drag intent has fired.
   */
  onCancelled?: () => void;
  /** Inline play/scrub on file tiles — do not arm hold or steal the tap. */
  shouldIgnoreTouch?: (target: EventTarget | null) => boolean;
};

/**
 * Touch long-press for mobile.
 * - Short `onPickup` (drag arm) then longer menu arm if still.
 * - Context menu opens when the hold **arms** (normal mobile long-press),
 *   not on finger release. firedRef stays true through touchend so click
 *   suppressors still ignore the synthetic click.
 * - Moving after pickup → drag intent, menu cancelled.
 * - Does not block normal taps.
 */
export function useLongPress(
  onLongPress: ((coords: Coords) => void) | undefined,
  {
    delay = DEFAULT_DELAY,
    pickupDelay = DEFAULT_PICKUP_DELAY,
    onPickup,
    onDragIntent,
    onMenuArmed,
    onCancelled,
    shouldIgnoreTouch,
  }: UseLongPressOptions = {},
) {
  const menuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef({ x: 0, y: 0 });
  const firedRef = useRef(false);
  const pickupFiredRef = useRef(false);
  const dragIntentFiredRef = useRef(false);
  /** A finger is down on this target and the hold is ours (not a mouse). */
  const touchActiveRef = useRef(false);
  const onLongPressRef = useRef(onLongPress);
  const onPickupRef = useRef(onPickup);
  const onDragIntentRef = useRef(onDragIntent);
  const onMenuArmedRef = useRef(onMenuArmed);
  const onCancelledRef = useRef(onCancelled);
  const shouldIgnoreTouchRef = useRef(shouldIgnoreTouch);
  onLongPressRef.current = onLongPress;
  onPickupRef.current = onPickup;
  onDragIntentRef.current = onDragIntent;
  onMenuArmedRef.current = onMenuArmed;
  onCancelledRef.current = onCancelled;
  shouldIgnoreTouchRef.current = shouldIgnoreTouch;

  const clearMenu = useCallback(() => {
    if (menuTimerRef.current) clearTimeout(menuTimerRef.current);
    menuTimerRef.current = null;
  }, []);

  const clearPickup = useCallback(() => {
    if (pickupTimerRef.current) clearTimeout(pickupTimerRef.current);
    pickupTimerRef.current = null;
  }, []);

  const clearPendingArm = useCallback(() => {
    if (pendingArmTimerRef.current) clearTimeout(pendingArmTimerRef.current);
    pendingArmTimerRef.current = null;
  }, []);

  const clear = useCallback(() => {
    clearPendingArm();
    clearMenu();
    clearPickup();
  }, [clearMenu, clearPickup, clearPendingArm]);

  /** Scroll/pan won — drop timers + pickup; never after a real drag starts. */
  const abortForScroll = useCallback(() => {
    if (dragIntentFiredRef.current) return false;
    const hadHold =
      Boolean(menuTimerRef.current) ||
      Boolean(pickupTimerRef.current) ||
      pickupFiredRef.current ||
      firedRef.current;
    firedRef.current = false;
    pickupFiredRef.current = false;
    clear();
    if (hadHold) onCancelledRef.current?.();
    return true;
  }, [clear]);

  // If the row unmounts mid-hold (live list data), pending timers must not
  // fire onPickup/onMenuArmed against a dead component.
  useEffect(() => clear, [clear]);

  const armHoldTimers = useCallback(
    (menuDelay: number, dragPickupDelay: number | undefined) => {
      if (onPickupRef.current && dragPickupDelay != null) {
        pickupTimerRef.current = setTimeout(() => {
          pickupFiredRef.current = true;
          onPickupRef.current?.({
            x: startRef.current.x,
            y: startRef.current.y,
          });
        }, dragPickupDelay);
      }

      if (onLongPressRef.current) {
        menuTimerRef.current = setTimeout(() => {
          menuTimerRef.current = null;
          // firedRef already true = Android `contextmenu` opened it first.
          if (dragIntentFiredRef.current || firedRef.current) return;
          firedRef.current = true;
          const coords = {
            x: startRef.current.x,
            y: startRef.current.y,
          };
          onMenuArmedRef.current?.(coords);
          onLongPressRef.current?.(coords);
        }, menuDelayAfterArm(menuDelay));
      }
    },
    [],
  );

  const scheduleHoldArm = useCallback(
    (menuDelay: number, dragPickupDelay: number | undefined) => {
      clearPendingArm();
      pendingArmTimerRef.current = setTimeout(() => {
        pendingArmTimerRef.current = null;
        armHoldTimers(menuDelay, dragPickupDelay);
      }, HOLD_ARM_DELAY_MS);
    },
    [armHoldTimers, clearPendingArm],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!onLongPressRef.current && !onPickupRef.current) return;
      if (shouldIgnoreTouchRef.current?.(e.target)) return;
      firedRef.current = false;
      pickupFiredRef.current = false;
      dragIntentFiredRef.current = false;
      const t = e.touches[0];
      if (!t) return;
      touchActiveRef.current = true;
      startRef.current = { x: t.clientX, y: t.clientY };
      clear();
      scheduleHoldArm(
        delay,
        onPickupRef.current ? pickupDelay : undefined,
      );
    },
    [clear, delay, pickupDelay, scheduleHoldArm],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const armed =
        Boolean(pendingArmTimerRef.current) ||
        Boolean(menuTimerRef.current) ||
        Boolean(pickupTimerRef.current) ||
        pickupFiredRef.current;
      if (!armed) return;
      if (dragIntentFiredRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = Math.abs(t.clientX - startRef.current.x);
      const dy = Math.abs(t.clientY - startRef.current.y);
      const dist = Math.max(dx, dy);

      if (!pickupFiredRef.current) {
        if (dist > PRE_PICKUP_MOVE) {
          clearPendingArm();
          abortForScroll();
        }
        return;
      }

      if (dist <= POST_PICKUP_DRAG_MOVE) return;

      clearMenu();
      firedRef.current = false;
      pickupFiredRef.current = false;
      dragIntentFiredRef.current = true;
      onDragIntentRef.current?.({ x: t.clientX, y: t.clientY });
    },
    [abortForScroll, clearMenu, clearPendingArm],
  );

  const onTouchEnd = useCallback(() => {
    touchActiveRef.current = false;
    clear();
    // Menu already opened on arm. Keep firedRef true briefly so row click
    // / open handlers still see longPressFired() and ignore the synthetic click.
    if (firedRef.current) {
      window.setTimeout(() => {
        firedRef.current = false;
      }, 320);
      return;
    }
    firedRef.current = false;
  }, [clear]);

  const onTouchCancel = useCallback(() => {
    touchActiveRef.current = false;
    firedRef.current = false;
    clear();
  }, [clear]);

  /**
   * Call first in the target's `onContextMenu`. Android also fires
   * `contextmenu` on a held finger (~400-500ms), so a row with both handlers
   * opened its menu twice. Returns true when this touch hold already opened the
   * menu (caller must not open it again). If `contextmenu` wins the race, the
   * pending hold timer is dropped and the caller opens it, exactly once.
   * Mouse/pen right-click never has an active touch, so it returns false.
   */
  const holdOwnsContextMenu = useCallback(() => {
    if (!touchActiveRef.current) return false;
    if (firedRef.current) return true;
    clearMenu();
    // Marks the hold as used: a menu timer armed later skips, and the
    // synthetic click after release is still ignored by longPressFired().
    firedRef.current = true;
    return false;
  }, [clearMenu]);

  const active = Boolean(onLongPress || onPickup || onDragIntent);
  const nativeNodeRef = useRef<HTMLElement | null>(null);
  const detachNativeRef = useRef<(() => void) | null>(null);
  const delayRef = useRef(delay);
  const pickupDelayRef = useRef(pickupDelay);
  delayRef.current = delay;
  pickupDelayRef.current = pickupDelay;

  // Native + passive, attached in the ref callback — no setState remount.
  const attachNative = useCallback(
    (node: HTMLElement) => {
      const start = (e: TouchEvent) => {
        if (!onLongPressRef.current && !onPickupRef.current) return;
        firedRef.current = false;
        pickupFiredRef.current = false;
        dragIntentFiredRef.current = false;
        const t = e.touches[0];
        if (!t) return;
        touchActiveRef.current = true;
        startRef.current = { x: t.clientX, y: t.clientY };
        clear();
        pendingArmTimerRef.current = setTimeout(() => {
          pendingArmTimerRef.current = null;
          if (onPickupRef.current && pickupDelayRef.current != null) {
            pickupTimerRef.current = setTimeout(() => {
              pickupFiredRef.current = true;
              onPickupRef.current?.({
                x: startRef.current.x,
                y: startRef.current.y,
              });
            }, pickupDelayRef.current);
          }
          if (onLongPressRef.current) {
            menuTimerRef.current = setTimeout(() => {
              menuTimerRef.current = null;
              if (dragIntentFiredRef.current || firedRef.current) return;
              firedRef.current = true;
              const coords = {
                x: startRef.current.x,
                y: startRef.current.y,
              };
              onMenuArmedRef.current?.(coords);
              onLongPressRef.current?.(coords);
            }, menuDelayAfterArm(delayRef.current));
          }
        }, HOLD_ARM_DELAY_MS);
      };

      let scrollPort: Element | null = null;
      const detachScroll = () => {
        scrollPort?.removeEventListener("scroll", onScroll);
        scrollPort = null;
      };
      function scrollableListCanPan(dy: number, dx: number) {
        if (!(scrollPort instanceof HTMLElement)) return false;
        if (Math.abs(dy) < Math.abs(dx)) return false;
        const max = scrollPort.scrollHeight - scrollPort.clientHeight;
        if (max <= 1) return false;
        const top = scrollPort.scrollTop;
        // Finger down → content follows → scrollTop decreases.
        if (dy > 0 && top > 0) return true;
        // Finger up → scrollTop increases.
        if (dy < 0 && top < max - 1) return true;
        return false;
      }
      function onScroll() {
        clearPendingArm();
        // Scroll always wins over hold/menu. Active file-drag keeps going.
        if (!abortForScroll()) return;
        detachScroll();
      }
      const move = (e: TouchEvent) => {
        const armed =
          Boolean(pendingArmTimerRef.current) ||
          Boolean(menuTimerRef.current) ||
          Boolean(pickupTimerRef.current) ||
          pickupFiredRef.current;
        if (!armed) return;
        if (dragIntentFiredRef.current) return;
        const t = e.touches[0];
        if (!t) return;
        const dx = t.clientX - startRef.current.x;
        const dy = t.clientY - startRef.current.y;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);
        const dist = Math.max(adx, ady);

        if (!pickupFiredRef.current) {
          const verticalPan =
            ady >= PAN_SLOP_PX && ady > adx && scrollableListCanPan(dy, dx);
          if (dist > PRE_PICKUP_MOVE || verticalPan) {
            clearPendingArm();
            abortForScroll();
          }
          return;
        }

        if (dist <= POST_PICKUP_DRAG_MOVE) return;

        // Vertical pan on a list that can still scroll → never steal into drag.
        if (scrollableListCanPan(dy, dx)) {
          abortForScroll();
          return;
        }

        clearMenu();
        firedRef.current = false;
        pickupFiredRef.current = false;
        dragIntentFiredRef.current = true;
        onDragIntentRef.current?.({ x: t.clientX, y: t.clientY });
      };

      const end = () => {
        touchActiveRef.current = false;
        clear();
        if (firedRef.current) {
          window.setTimeout(() => {
            firedRef.current = false;
          }, 320);
          return;
        }
        firedRef.current = false;
      };

      const cancel = () => {
        touchActiveRef.current = false;
        firedRef.current = false;
        clear();
      };

      const startWithScroll = (e: TouchEvent) => {
        if (shouldIgnoreTouchRef.current?.(e.target)) return;
        start(e);
        detachScroll();
        scrollPort = node.closest(
          ".desk-file-tree-scroll, .studio-dm-conversations, .studio-dm-scroll, .studio-mail-list, .studio-mail-body",
        );
        scrollPort?.addEventListener("scroll", onScroll, { passive: true });
      };
      const endWithScroll = () => {
        detachScroll();
        end();
      };
      const cancelWithScroll = () => {
        detachScroll();
        cancel();
      };

      node.addEventListener("touchstart", startWithScroll, { passive: true });
      node.addEventListener("touchmove", move, { passive: true });
      node.addEventListener("touchend", endWithScroll, { passive: true });
      node.addEventListener("touchcancel", cancelWithScroll, { passive: true });
      return () => {
        node.removeEventListener("touchstart", startWithScroll);
        node.removeEventListener("touchmove", move);
        node.removeEventListener("touchend", endWithScroll);
        node.removeEventListener("touchcancel", cancelWithScroll);
        detachScroll();
        clear();
      };
    },
    [abortForScroll, clear, clearMenu, clearPendingArm],
  );

  const longPressRef = useCallback(
    (node: HTMLElement | null) => {
      if (nativeNodeRef.current === node) return;
      detachNativeRef.current?.();
      detachNativeRef.current = null;
      nativeNodeRef.current = node;
      if (node && active) detachNativeRef.current = attachNative(node);
    },
    [active, attachNative],
  );

  useEffect(
    () => () => {
      detachNativeRef.current?.();
      detachNativeRef.current = null;
    },
    [],
  );

  return {
    longPressRef,
    longPressHandlers: active
      ? { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel }
      : {},
    longPressFired: () => firedRef.current,
    clearLongPressFired: () => {
      firedRef.current = false;
    },
    pickupFired: () => pickupFiredRef.current,
    dragIntentFired: () => dragIntentFiredRef.current,
    cancelMenu: clearMenu,
    holdOwnsContextMenu,
  };
}
